import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  Row,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiQuestionnaireLayout,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {WEIGHT_LOSS_KEYS, YES_OPTION} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setQuestionnaireAnswer, setNextQuestionnaire} from '~/redux/questionnaires/slice';
import {Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const initSelectedYesOptions = (arr: Relation[]) => {
  return arr.reduce((res: any, cur: Relation) => {
    if (cur?.key) {
      res[cur?.key] = cur?.value ? cur?.value : '';
      return res;
    }
  }, {});
};

const Questionnaire16Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === WEIGHT_LOSS_KEYS.TAKE_WEIGHT_LOSS_MEDICATION_BEFORE);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[WEIGHT_LOSS_KEYS.TAKE_WEIGHT_LOSS_MEDICATION_BEFORE]?.data,
  );

  const {inputs, setInputs, trimSpaces} = useInput({
    selectedOption: currentAnswer?.selectedOption || '',
    selectedYesOptions: currentAnswer?.selectedYesOptions?.length
      ? initSelectedYesOptions(currentAnswer?.selectedYesOptions)
      : {},
  });
  const isDisabled =
    !inputs.selectedOption ||
    (inputs.selectedOption === YES_OPTION &&
      !(
        Object.keys(inputs.selectedYesOptions).length > 1 &&
        !Object.keys(inputs.selectedYesOptions).some(i => trimSpaces(inputs.selectedYesOptions[i]).length < 1)
      ));

  const onClickSubmit = () => {
    dispatch(
      setQuestionnaireAnswer({
        key: WEIGHT_LOSS_KEYS.TAKE_WEIGHT_LOSS_MEDICATION_BEFORE,
        index: questionnaire?.index || '',
        data: {
          ...inputs,
          selectedYesOptions:
            inputs.selectedOption === YES_OPTION
              ? Object.keys(inputs.selectedYesOptions).map(val => ({
                  key: val,
                  value: trimSpaces(inputs.selectedYesOptions[val]),
                }))
              : [],
        },
      }),
    );
    if (inputs?.selectedOption === YES_OPTION) {
      dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.DID_MEDICATION_WORK}));
    } else {
      dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY}));
    }
  };

  const renderRelations = () => {
    if (inputs?.selectedOption === YES_OPTION) {
      const relations = questionnaire?.options?.find(option => option.value === YES_OPTION)?.relations;
      return (
        <>
          {relations?.map(relation => (
            <Col key={relation.key} w="100%">
              <UiText fontWeight={700} fontSize="16px" mt={10}>
                {relation?.questions?.default}
              </UiText>
              <UiFormControl id="medicationsTaking" isRequired>
                <UiTextarea
                  mt={spacing.small}
                  bg="white"
                  borderRadius={spacing.tiny}
                  placeholder="Type your response here..."
                  defaultValue={inputs.selectedYesOptions[relation?.key]}
                  onChange={({target}) =>
                    setInputs({selectedYesOptions: {...inputs.selectedYesOptions, [relation?.key]: target.value}})
                  }
                />
              </UiFormControl>
            </Col>
          ))}
        </>
      );
    }
  };
  return (
    <UiQuestionnaireLayout
      backIcon
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      disableNext={isDisabled}
    >
      <Col align="center" textAlign="center" h="100%">
        <UiText variant="mdqTitle" my={10}>
          {questionnaire?.questions?.default}
        </UiText>
        <Row justifyContent="center" w="100%">
          {questionnaire?.options?.map(value => (
            <UiButton
              onClick={() => setInputs({selectedOption: value?.value})}
              key={`${value.label}-${value.value}`}
              title={value?.label}
              mb={4}
              variant={inputs?.selectedOption === value.value ? 'solid' : 'toggle'}
              w="100%"
              mx={1}
            />
          ))}
        </Row>
        {renderRelations()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire16Screen;
